import { CircularProgress } from "@mui/material";
import classNames from "classnames";
import Icon from "components/Icons/Icon";
import type Icons from "components/Icons/Icons.json";
import { Link } from "react-router-dom";
import "./PrimaryButton.scss";

export type PrimaryButtonProps = {
	type?: "solid" | "outlined" | "icon" | "isotone";
	label?: React.ReactNode;
	url?: string;
	link?: string;
	color?: "green" | "magenta" | "orange" | "purple" | "black" | "white" | "grey" | "light";
	icon?: keyof typeof Icons;
	iconPosition?: "front" | "end";
	iconSize?: number;
	size?: "xs" | "small" | "medium" | "large" | "xl";
	radius?: "normal" | "small";
	onClick?: any;
	disabled?: boolean;
	title?: string;
	className?: string;
	loading?: boolean;
	style?: any;
	id?: string;
	testId?: string;
};

const PrimaryButton: React.FC<PrimaryButtonProps> = ({
	type = "solid",
	label,
	color,
	icon,
	iconPosition = "front",
	iconSize,
	size,
	radius = "normal",
	url,
	link,
	onClick,
	disabled = false,
	title = "",
	className,
	loading,
	style,
	id,
	testId,
}) => {
	const renderButtonContent = () => {
		if (loading) {
			return <CircularProgress size={20} style={{ color: "white" }} />;
		}
		return (
			<>
				{icon && iconPosition === "front" ? <Icon name={icon} size={iconSize ? iconSize : 16} /> : null}
				{label}
				{icon && iconPosition === "end" ? <Icon name={icon} size={iconSize ? iconSize : 16} /> : null}
			</>
		);
	};
	const buttonColor =
		color === "green" ? "primary-1" : color === "orange" ? "primary-3" : color === "magenta" ? "primary-2" : color === "purple" ? "primary-4" : color;
	const buttonClasses = classNames(`btn-rounded icon-${iconPosition}`, type, buttonColor, size, className);

	// radius class: 'radius-small' reduces border-radius to 0.5rem
	const radiusClass = radius === "small" ? "radius-small" : undefined;
	const finalClasses = classNames(buttonClasses, radiusClass);

	return url ? (
		<a id={id} data-testid={testId} className={finalClasses} href={url} target="_blank" onClick={onClick} rel="noreferrer">
			<span>{renderButtonContent()}</span>
		</a>
	) : link ? (
		<Link id={id} data-testid={testId} className={finalClasses} to={link} title={title}>
			{renderButtonContent()}
		</Link>
	) : onClick ? (
		<button id={id} data-testid={testId} onClick={onClick} className={finalClasses} disabled={disabled || loading} title={title} style={style}>
			{renderButtonContent()}
		</button>
	) : (
		<button id={id} data-testid={testId} className={finalClasses} style={style}>
			{renderButtonContent()}
		</button>
	);
};

export default PrimaryButton;
